import React from "react";
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";

const CashOutDialog = ({
  open,
  onClose,
  cashOutAmount,
  onCashOutAmountChange,
  businessName,
  walletData,
  locationCurrency,
  locationCurrencySymbol,
  formatCurrency,
  isDisabled,
  isLoading,
  locationId,
  lastCashOutDate,
  onCashOut,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            textTransform: "lowercase",
            color: "#7c28d6",
          }}
        >
          cash out
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "#666" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {/* Balance Card - Same style as main balance card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            m: { xs: 2, sm: 2.5 },
            bgcolor: "rgba(242, 233, 255, 1)",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  color: "#222",
                  textTransform: "lowercase",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                {businessName}
              </Typography>
              <StarIcon sx={{ color: "#7c28d6", fontSize: { xs: 16, sm: 18 } }} />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                mb: 0.5,
                textTransform: "lowercase",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              current balance
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#222",
                mb: 0.5,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              }}
            >
              {formatCurrency(walletData?.balance || 0, locationCurrency)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                textTransform: "lowercase",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              {locationCurrency?.toLowerCase() || "usd"}
            </Typography>
          </Box>
        </Paper>

        {/* Cash Out Amount Input */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2 }}>
          <TextField
            fullWidth
            label="enter cash out amount"
            value={cashOutAmount}
            onChange={onCashOutAmountChange}
            type="number"
            disabled={isDisabled}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: "#666", fontWeight: 500 }}>
                    {locationCurrencySymbol || "₹"}
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                bgcolor: "#fff",
              },
              "& .MuiInputLabel-root": {
                textTransform: "lowercase",
              },
            }}
          />

          {/* Last Cash Out Information */}
          {lastCashOutDate && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  mb: 0.5,
                  textTransform: "lowercase",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                last cash out on {lastCashOutDate}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  textTransform: "lowercase",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                you can only cash out once per week
              </Typography>
            </Box>
          )}

          {/* Cash Out Button */}
          <Button
            variant="contained"
            fullWidth
            disabled={
              isDisabled || 
              !cashOutAmount || 
              parseFloat(cashOutAmount) <= 0 || 
              parseFloat(cashOutAmount) > (walletData?.balance || 0) / 100 || 
              isLoading
            }
            onClick={onCashOut}
            sx={{
              bgcolor: "#7c28d6",
              color: "#fff",
              textTransform: "lowercase",
              py: { xs: 1, sm: 1.25 },
              borderRadius: 1.5,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontWeight: 600,
              "&:hover": {
                bgcolor: "#6a1fb8",
              },
              "&:disabled": {
                bgcolor: "#ccc",
                color: "#999",
              },
            }}
          >
            {isLoading ? "processing..." : "cash out"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CashOutDialog;

